import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, Check, Shield, Gauge, Cpu, Activity, Lock, Infinity as InfinityIcon, Server, Gift, Crown } from "lucide-react";
import { api } from "../lib/api";
import { planStyle, fmtStorage } from "../lib/planStyles";
import { useLang, LangSelect, panelRentalEur, PANEL_RENTAL_TIERS } from "../context/LanguageContext";

const PINGS = [
  ["São Paulo", 8], ["Rio de Janeiro", 12], ["Buenos Aires", 28], ["Miami", 45], ["Lisboa", 92],
];

export function PanelRentalCard({ dataTestid = "panel-rental-card" }) {
  const { t, fmtPrice } = useLang();
  const tiers = [
    { label: t("rental_up_to_50"), eur: PANEL_RENTAL_TIERS[0].eur },
    { label: t("rental_50_100"), eur: PANEL_RENTAL_TIERS[1].eur },
    { label: t("rental_over_100"), eur: PANEL_RENTAL_TIERS[2].eur },
  ];
  return (
    <div data-testid={dataTestid} className="glass rounded-2xl p-6 border border-amber-400/40 bg-gradient-to-r from-amber-400/10 via-transparent to-cyan-400/10 anim-fade-up">
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
            <Crown size={20} className="text-amber-300" />
          </div>
          <p className="font-display text-white font-bold text-sm leading-snug">{t("rental_title")}</p>
        </div>
        <div className="flex flex-wrap gap-2.5 flex-1">
          {tiers.map((tier) => (
            <span key={tier.label} className="inline-flex items-center gap-1.5 text-[11px] font-mono2 border border-amber-400/30 bg-amber-400/10 rounded-full px-3 py-1.5 text-slate-300">
              {tier.label}: <b className="text-amber-300">{fmtPrice(tier.eur)}{t("per_month")}</b>
            </span>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">+ {t("rental_plus")} · {t("rental_note")}</p>
    </div>
  );
}

export default function Landing() {
  const { t, fmtPrice } = useLang();
  const [plans, setPlans] = useState([]);
  const [tick, setTick] = useState(0);
  const [tb, setTb] = useState(11);
  const [customPrice, setCustomPrice] = useState(null);

  useEffect(() => {
    api.get("/saas/plans").then((r) => setPlans(r.data)).catch(() => {});
    const tt = setInterval(() => setTick((v) => v + 1), 2000);
    return () => clearInterval(tt);
  }, []);

  useEffect(() => {
    const tt = setTimeout(() => {
      api.get(`/saas/plans/suggest?storage_gb=${tb * 1024}`).then((r) => setCustomPrice(r.data.price_eur)).catch(() => {});
    }, 200);
    return () => clearTimeout(tt);
  }, [tb]);

  const jitter = (base, amp) => (base + Math.sin(tick * 1.7 + base) * amp).toFixed(1);

  const PILLARS = [
    { icon: Cpu, title: t("p1_t"), desc: t("p1_d"), span: "md:col-span-8", accent: "text-cyan-400 border-cyan-400/25 bg-cyan-400/10" },
    { icon: InfinityIcon, title: t("p2_t"), desc: t("p2_d"), span: "md:col-span-4", accent: "text-emerald-400 border-emerald-400/25 bg-emerald-400/10" },
    { icon: Shield, title: t("p3_t"), desc: t("p3_d"), span: "md:col-span-4", accent: "text-rose-400 border-rose-400/25 bg-rose-400/10" },
    { icon: Lock, title: t("p4_t"), desc: t("p4_d"), span: "md:col-span-8", accent: "text-amber-400 border-amber-400/25 bg-amber-400/10" },
  ];

  const customTotal = customPrice ? customPrice + panelRentalEur(tb * 1024) : null;

  return (
    <div className="min-h-screen">
      <div className="bg-scene" />

      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-zinc-800/60 h-16 px-4 sm:px-8 flex items-center justify-between">
        <div className="flex items-center gap-2.5" data-testid="landing-logo">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center">
            <Zap size={16} className="text-zinc-950" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-white tracking-tight">CINE<span className="neon-text">BOT</span></span>
          <span className="hidden sm:inline-flex items-center gap-1.5 ml-3 text-[9px] font-mono2 uppercase tracking-widest text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t("online")}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <LangSelect testid="lang-select-landing" />
          <a data-testid="landing-nav-plans-btn" href="#planos" className="hidden sm:inline text-sm text-slate-300 hover:text-white transition-colors px-3 py-2">{t("nav_plans")}</a>
          <Link data-testid="landing-nav-login-btn" to="/login" className="btn-ghost-dark text-sm font-semibold text-white px-5 py-2.5 rounded-xl">{t("nav_login")}</Link>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-4 sm:px-8 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <div className="anim-fade-up">
          <p className="text-xs font-mono2 uppercase tracking-[0.35em] text-cyan-400 mb-5 inline-flex items-center gap-2">
            <Activity size={13} /> {t("hero_eyebrow")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            {t("hero_t1")}<br />{t("hero_t2")}<br /><span className="neon-text">{t("hero_t3")}</span>
          </h1>
          <p className="mt-6 text-base text-slate-400 max-w-lg leading-relaxed">
            {t("hero_sub")}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a data-testid="hero-subscribe-cta" href="#planos" className="btn-neon px-7 py-3.5 rounded-xl text-sm inline-flex items-center gap-2">
              {t("cta_plans")} <ArrowRight size={16} />
            </a>
            <Link data-testid="hero-cta-account" to="/login" className="btn-ghost-dark px-7 py-3.5 rounded-xl text-sm font-semibold text-white inline-flex items-center">
              {t("cta_account")}
            </Link>
          </div>
          <div className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2" data-testid="trial-badge">
            <Gift size={14} className="text-emerald-300 shrink-0" />
            <span className="text-xs text-emerald-200">{t("trial_badge")}</span>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-2"><Cpu size={14} className="text-cyan-400" /> {t("f_nvme")}</span>
            <span className="inline-flex items-center gap-2"><InfinityIcon size={14} className="text-emerald-400" /> {t("f_stream")}</span>
            <span className="inline-flex items-center gap-2"><Shield size={14} className="text-rose-400" /> {t("f_ddos")}</span>
          </div>
        </div>

        <div className="anim-fade-up anim-delay-2 hidden lg:block">
          <div className="glass rounded-3xl p-6 anim-pulse-glow font-mono2" data-testid="hero-telemetry">
            <div className="flex items-center justify-between mb-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 inline-flex items-center gap-2"><Server size={12} className="text-cyan-400" /> {t("telemetry")}</p>
              <span className="text-[9px] uppercase tracking-widest text-emerald-300 inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> {t("live")}</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-4">
                <p className="text-[9px] uppercase tracking-widest text-slate-500">{t("throughput")}</p>
                <p className="text-2xl font-bold text-cyan-300 mt-1" data-testid="telemetry-throughput">{jitter(38.4, 2.5)} <span className="text-xs text-slate-500">Gbps</span></p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-4">
                <p className="text-[9px] uppercase tracking-widest text-slate-500">{t("latency")}</p>
                <p className="text-2xl font-bold text-emerald-300 mt-1">{jitter(11, 2)} <span className="text-xs text-slate-500">ms</span></p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-4">
                <p className="text-[9px] uppercase tracking-widest text-slate-500">{t("buffering")}</p>
                <p className="text-2xl font-bold text-white mt-1">0.0<span className="text-xs text-slate-500">%</span></p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-4">
                <p className="text-[9px] uppercase tracking-widest text-slate-500">{t("uptime")}</p>
                <p className="text-2xl font-bold text-amber-300 mt-1">99.99<span className="text-xs text-slate-500">%</span></p>
              </div>
            </div>
            <div className="mt-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-4 space-y-1.5">
              {PINGS.map(([city, ms]) => (
                <div key={city} className="flex items-center gap-3 text-[11px]">
                  <span className="text-slate-500 w-28 truncate">{city}</span>
                  <div className="flex-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all duration-700" style={{ width: `${Math.min(100, ms)}%` }} />
                  </div>
                  <span className="text-cyan-300 w-12 text-right">{ms} ms</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 sm:px-8 max-w-7xl mx-auto pb-20">
        <div className="grid md:grid-cols-12 gap-5">
          {PILLARS.map((f, i) => (
            <div key={f.title} data-testid={`pillar-card-${i}`} className={`col-span-full ${f.span} glass glass-hover rounded-2xl p-6 anim-fade-up anim-delay-${i + 1}`}>
              <div className={`w-11 h-11 rounded-xl border flex items-center justify-center mb-4 ${f.accent}`}>
                <f.icon size={20} />
              </div>
              <h3 className="font-display text-white font-semibold text-sm mb-2">{f.title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="planos" className="px-4 sm:px-8 max-w-7xl mx-auto pb-24">
        <div className="text-center mb-10 anim-fade-up">
          <p className="text-xs font-mono2 uppercase tracking-[0.35em] text-cyan-400 mb-3">{t("plans_eyebrow")}</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t("plans_title1")} <span className="neon-text">{t("plans_title2")}</span></h2>
          <p className="text-sm text-slate-500 mt-3 inline-flex items-center gap-2"><Gauge size={14} className="text-emerald-400" /> {t("plans_sub")}</p>
          <p className="text-xs text-slate-400 mt-2 inline-flex items-center gap-2"><Gift size={13} className="text-emerald-400" /> {t("plans_trial")}</p>
        </div>

        <div className="mb-6">
          <PanelRentalCard dataTestid="panel-rental-landing" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((p) => {
            const s = planStyle(p.color);
            const key = p.id.replace("plan_", "");
            const storageEur = p.price_eur ?? p.price_brl ?? 0;
            const rent = panelRentalEur(p.storage_gb);
            const total = storageEur + rent;
            return (
              <div key={p.id} data-testid={`plan-card-${key}`} className={`glass rounded-2xl p-7 border ${s.ring} ${s.glow} transition-all duration-300 flex flex-col anim-fade-up`}>
                <div className="flex items-center justify-between">
                  <p className="font-display text-white font-semibold">{p.name}</p>
                  {p.badge && <span className={`text-[9px] font-mono2 uppercase tracking-widest border rounded-full px-2.5 py-1 ${s.chip}`}>{p.badge}</span>}
                </div>
                <p className={`font-display text-3xl font-extrabold mt-4 ${s.text}`} data-testid={`plan-total-${key}`}>{fmtPrice(total)}<span className="text-sm text-slate-500 font-normal">{t("per_month")}</span></p>
                <p className="text-[10px] text-slate-500 mt-1 font-mono2" data-testid={`plan-breakdown-${key}`}>{fmtPrice(storageEur)} {t("storage_word")} + {fmtPrice(rent)} {t("panel_word")}</p>
                <p className="text-xs text-slate-400 mt-1 font-mono2">{fmtStorage(p.storage_gb)} {t("nvme_storage")}</p>
                <ul className="mt-5 space-y-2 text-xs text-slate-400 flex-1">
                  <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_stream")}</li>
                  <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_bots")}</li>
                  <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_links")}</li>
                  <li className="flex items-center gap-2"><Check size={13} className="text-emerald-400" /> {t("pf_telegram")}</li>
                </ul>
                <Link data-testid={`plan-subscribe-btn-${key}`} to="/login" className={`mt-6 text-center w-full py-3 rounded-xl text-sm font-bold transition-colors ${s.btn}`}>
                  {t("subscribe")} {p.name}
                </Link>
              </div>
            );
          })}
        </div>
        <div className="glass rounded-2xl p-7 mt-6 border border-cyan-400/25 anim-fade-up" data-testid="custom-plan-card">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <p className="font-display text-white font-semibold">{t("custom_title")}</p>
                <span className="text-[9px] font-mono2 uppercase tracking-widest text-cyan-300 border border-cyan-400/30 bg-cyan-400/10 rounded-full px-2.5 py-1">{t("custom_badge")}</span>
              </div>
              <p className="text-xs text-slate-400 mb-4">{t("custom_desc")}</p>
              <input data-testid="custom-tb-slider" type="range" min="11" max="200" step="1" value={tb} onChange={(e) => setTb(Number(e.target.value))} className="w-full accent-cyan-400" />
              <div className="flex justify-between text-[10px] font-mono2 text-slate-500 mt-1"><span>11 TB</span><span>200 TB</span></div>
            </div>
            <div className="text-center lg:text-right shrink-0">
              <p className="font-display text-3xl font-extrabold text-cyan-300" data-testid="custom-tb-value">{tb} TB</p>
              <p className="font-display text-xl font-bold text-white mt-1" data-testid="custom-price">{customTotal ? `${fmtPrice(customTotal)}${t("per_month")}` : "…"}</p>
              {customPrice && (
                <p className="text-[10px] text-slate-500 font-mono2 mt-0.5" data-testid="custom-breakdown">{fmtPrice(customPrice)} {t("storage_word")} + {fmtPrice(panelRentalEur(tb * 1024))} {t("panel_word")}</p>
              )}
              <Link data-testid="custom-plan-cta" to="/login" className="btn-neon px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2 mt-4">
                {t("custom_cta")} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800/80 bg-zinc-950 px-8 py-12 text-center">
        <p className="font-display text-lg font-bold text-white/10 tracking-[0.3em] select-none">CINEBOT</p>
        <p className="text-xs text-zinc-500 font-mono2 mt-3">{t("footer_note")}</p>
      </footer>
    </div>
  );
}
